import {
  Plugin,
  PluginFile,
  UserRole,
  ViewFileName,
} from "@fuse/recoil/pluginStudio/types"

/**
 * ApiPlugin interface representing the plugin data structure from the API
 */
export interface ApiPlugin {
  id: string
  code?: {
    frontendGuestCodeWeb?: string
    frontendHostViewCodeWeb?: string
    frontendHostConfigCodeWeb?: string
    transpiledFrontendGuestCodeWeb?: string
    transpiledFrontendHostViewCodeWeb?: string
    transpiledFrontendHostConfigCodeWeb?: string
    customViews?: {
      [viewName: string]: {
        sourceCode?: string
        transpiledCode?: string
        visibleToRole?: string
        fileName?: string
      }
    }
  }
  createdAt?: {
    _seconds: number
    _nanoseconds: number
  }
  createdBy?: string
  enabled?: boolean
  manifest?: {
    name?: string
    description?: string
    version?: string
    author?: string
    requestedData?: string[]
    hooks?: string[]
    triggers?: string[]
    searchTags?: string[]
    [key: string]: any
    iconName?: string
  }
  updatedAt?: {
    _seconds: number
    _nanoseconds: number
  }
  updatedBy?: string
  [key: string]: any
}

/**
 * Converts an API plugin object to the internal Plugin format for Plugin Studio
 *
 * UPDATED: This function now returns a Plugin that matches the
 * Plugin interface from @fuse/recoil/pluginStudio/types, with all required properties:
 * - id: string
 * - name: string
 * - description: string
 * - manifest: object
 * - files: PluginFile[]
 * - createdAt: number
 *
 * @param apiPlugin - Plugin data from the API
 * @returns Plugin in the internal format for Plugin Studio
 */
export function convertApiPluginToInternal(apiPlugin: ApiPlugin): Plugin {
  console.log(
    "[pluginConverters] Converting API plugin to internal Plugin Studio format:",
    {
      pluginId: apiPlugin.id,
      hasManifest: !!apiPlugin.manifest,
      hasCode: !!apiPlugin.code,
    },
  )

  if (!apiPlugin || !apiPlugin.id) {
    console.error("[pluginConverters] Invalid API plugin object", apiPlugin)
    throw new Error("Invalid API plugin object")
  }

  const timestamp = Date.now()

  // Default manifest if not provided
  const manifest = {
    name: apiPlugin.manifest?.name || "Unnamed Plugin",
    description: apiPlugin.manifest?.description || "No description provided",
    version: apiPlugin.manifest?.version || "1.0.0",
    author: apiPlugin.manifest?.author || "Unknown",
    searchTags: apiPlugin.manifest?.searchTags || [],
    iconName: apiPlugin.manifest?.iconName || "lightning",
    ...(apiPlugin.manifest || {}),
  }

  // Create files array from code properties
  const files: PluginFile[] = []

  // Add guest file if it exists
  if (apiPlugin.code?.frontendGuestCodeWeb) {
    files.push({
      id: `${apiPlugin.id}-guest`,
      name: ViewFileName.GUEST,
      path: `/frontend/${ViewFileName.GUEST}`,
      content: apiPlugin.code.frontendGuestCodeWeb,
      transpiledContent: apiPlugin.code.transpiledFrontendGuestCodeWeb || "",
      type: UserRole.GUEST,
      lastModified: timestamp,
    })
  }

  // Add host file if it exists
  if (apiPlugin.code?.frontendHostViewCodeWeb) {
    files.push({
      id: `${apiPlugin.id}-host`,
      name: ViewFileName.HOST_EVENT_VIEW,
      path: `/frontend/${ViewFileName.HOST_EVENT_VIEW}`,
      content: apiPlugin.code.frontendHostViewCodeWeb,
      transpiledContent: apiPlugin.code.transpiledFrontendHostViewCodeWeb || "",
      type: UserRole.HOST_EVENT_VIEW,
      lastModified: timestamp,
    })
  }

  // Add admin file if it exists
  if (apiPlugin.code?.frontendHostConfigCodeWeb) {
    files.push({
      id: `${apiPlugin.id}-admin`,
      name: ViewFileName.HOST_CONFIG_VIEW,
      path: `/frontend/${ViewFileName.HOST_CONFIG_VIEW}`,
      content: apiPlugin.code.frontendHostConfigCodeWeb,
      transpiledContent:
        apiPlugin.code.transpiledFrontendHostConfigCodeWeb || "",
      type: UserRole.HOST_CONFIG_VIEW,
      lastModified: timestamp,
    })
  }

  if (apiPlugin.code?.customViews) {
    Object.entries(apiPlugin.code.customViews).forEach(
      ([viewName, viewData]) => {
        if (
          viewData.sourceCode &&
          viewData.fileName &&
          viewData.visibleToRole
        ) {
          files.push({
            id: `${apiPlugin.id}-custom-${viewName}`,
            name: viewData.fileName,
            path: `/frontend/${viewData.fileName}`,
            content: viewData.sourceCode,
            transpiledContent: viewData.transpiledCode || "",
            type: viewData.visibleToRole as UserRole,
            lastModified: timestamp,
          })
        }
      },
    )
  }

  // Create the plugin object with all required Plugin Studio properties
  const plugin: Plugin = {
    id: apiPlugin.id,
    name: apiPlugin.manifest?.name || "Unnamed Plugin",
    description: apiPlugin.manifest?.description || "No description provided",
    manifest,
    files,
    createdAt: apiPlugin.createdAt?._seconds
      ? apiPlugin.createdAt._seconds * 1000
      : timestamp,
    code: apiPlugin.code
      ? {
          frontendGuestCodeWeb: apiPlugin.code.frontendGuestCodeWeb,
          frontendHostViewCodeWeb: apiPlugin.code.frontendHostViewCodeWeb,
          frontendHostConfigCodeWeb: apiPlugin.code.frontendHostConfigCodeWeb,
          transpiledFrontendGuestCodeWeb:
            apiPlugin.code.transpiledFrontendGuestCodeWeb,
          transpiledFrontendHostViewCodeWeb:
            apiPlugin.code.transpiledFrontendHostViewCodeWeb,
          transpiledFrontendHostConfigCodeWeb:
            apiPlugin.code.transpiledFrontendHostConfigCodeWeb,
          customViews: apiPlugin.code.customViews,
        }
      : undefined,
  }

  // Log the final converted plugin
  console.log(
    "[pluginConverters] Successfully converted to Plugin Studio format:",
    {
      id: plugin.id,
      name: plugin.name,
      description: plugin.description,
      hasManifest: !!plugin.manifest,
      hasFiles: Array.isArray(plugin.files),
      filesCount: plugin.files.length,
      hasCreatedAt: !!plugin.createdAt,
      isPluginStudioCompatible: true,
    },
  )

  // Check if code is available (for validation)
  if (
    !plugin.code?.transpiledFrontendGuestCodeWeb &&
    !plugin.code?.transpiledFrontendHostViewCodeWeb &&
    !plugin.code?.transpiledFrontendHostConfigCodeWeb
  ) {
    console.warn(
      `[pluginConverters] Plugin ${plugin.id} has no transpiled code`,
    )
    // Don't throw error - we'll let components handle missing code
  }

  return plugin
}
